import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Grid, TextField } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProject, createProjectAchievement } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailField from "../../components/common/DetailField";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function ProjectAchievementCreatePage() {
  const { businessDomainId, businessUnitId, projectId, goalId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/goals/${goalId}`;

  const [goal, setGoal] = useState(null);
  const [form, setForm] = useState({ actualValue: "", actualDate: "" });

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchProject(projectId);
        // goalId は配列インデックス
        const found = (d.goals || [])[Number(goalId)];
        setGoal(found || null);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, goalId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // 旧アプリ同様 goal フィールドにゴールオブジェクト全体を入れて送信
      await createProjectAchievement(projectId, {
        goal: goal,
        actualValue: form.actualValue,
        actualDate: form.actualDate ? `${form.actualDate}T00:00:00.000Z` : null,
        createdAchievementAt: new Date().toISOString(),
      });
      notify.success(t("message.create.success"));
      navigate(`${basePath}/achievements`);
    } catch (err) { notify.apiError(err); }
  };

  if (!goal) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("project.achievement.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("project.goal.indicator")} value={goal.indicator?.indicatorName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.unit")} value={goal.indicator?.unit} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.measuringMethod")} value={goal.indicator?.measuringMethod} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.evaluationStandard")} value={goal.indicator?.evaluationStandard} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.description")} value={goal.indicator?.description} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.plannedValue")} value={`${goal.plannedValue ?? ""}${goal.indicator?.unit ? `（${goal.indicator.unit}）` : ""}`} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.plannedDate")} value={formatDate(goal.plannedDate)} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.goal.deadline")} value={formatDate(goal.deadline)} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth required label={t("project.achievement.actualValue")} name="actualValue"
              value={form.actualValue} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth required type="date" label={t("project.achievement.actualDate")} name="actualDate"
              value={form.actualDate} onChange={handleChange} slotProps={{ inputLabel: { shrink: true } }} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/goals/${goalId}/achievements`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
